import { useEventViewItem } from '@google-tag-manager';
import { useLiveQuery } from '@sanity/preview-kit';
import { PageBuilder } from '@web-app/layout';
import { useEffect, useMemo } from 'react';

import { productContext } from '@core/context';
import { useLanguage, useQueryApi } from '@core/hook';
import { useRouting } from '@core/provider';
import { applyAbTest, getSanityPageRawQuery } from '@core/sanity';
import { RecentlyViewedProductsStorage } from '@core/storage';
import { ECountry, ERouting, PageSSR, ViewCMSProps } from '@core/type';
import {
  DeliveryCountriesEntity,
  EQueryKey,
  ForecastedDeliveryEntity,
  ProductPageEntity,
} from '@core/type/api';
import { SanityPageResponse, ProductShowQueryResult } from '@core/type/sanity';

import { SectionProductBlock } from './product-show';

export type ViewProductPageProps = PageSSR<
  ProductPageEntity &
    DeliveryCountriesEntity & {
      forecastedDeliveryData: ForecastedDeliveryEntity;
      userLocation: ECountry;
    },
  unknown,
  unknown
>;

export const ViewProductShow = ({
  cms,
  shouldUseVariation,
}: ViewCMSProps<ProductShowQueryResult>) => {
  const { query } = useRouting();
  const { language } = useLanguage();

  const { data: productApiData, isFetched } = useQueryApi<ViewProductPageProps, SanityPageResponse>(
    EQueryKey.PRODUCT_SHOW,
    cms,
    { enabled: !!cms },
  );

  useEventViewItem(productApiData);

  const [_, sanityQuery, params] = getSanityPageRawQuery(ERouting.PRODUCT_SHOW, query, language);
  const [data] = useLiveQuery(cms || null, sanityQuery, params);
  const cmsResult = applyAbTest(data, shouldUseVariation);
  const product = useMemo(() => ({ id: cmsResult.id }), [cmsResult.id]);

  useEffect(() => {
    RecentlyViewedProductsStorage.add(product.id);
  }, [product.id]);

  return (
    <>
      {isFetched && (
        <>
          <SectionProductBlock {...productApiData} medias={cmsResult.body.medias || []} />

          <productContext.Provider value={product}>
            <PageBuilder components={data.body.pageBuilder} pageId={data._id} />
          </productContext.Provider>
        </>
      )}
    </>
  );
};
